import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
} from '@mui/material'
import * as React from 'react'
import CloseIcon from '@mui/icons-material/Close'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import GroupIcon from '@mui/icons-material/Group'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LoginIcon from '@mui/icons-material/Login'
import LogoutIcon from '@mui/icons-material/Logout'
import ScheduleIcon from '@mui/icons-material/Schedule'
import { getForemanQC, updateForemanQC } from '../../services/inspectionsService'
import { ForemanQC } from '../../types/Work/ForemanQC'
import { getQCs } from '../../services/requestService'
import { SmallWorker } from '../../types/Employee/Employee'
import { useEffect, useState } from 'react'
import { SignOnDialog } from '../EmployeeToolsMenu/Attendance/SignOnDialog'
import { SignOutDialog } from '../EmployeeToolsMenu/Attendance/SignOutDialog'
import { useNavigate } from 'react-router-dom'
import { useProjectFeaturesContext } from '../project/contexts/ProjectFeaturesContext'
import { CardSignOnDialog } from '../EmployeeToolsMenu/Attendance/CardSignOnDialog'
import { CardSignOutDialog } from '../EmployeeToolsMenu/Attendance/CardSignOutDialog'
import { ConsentProvider } from '../EmployeeToolsMenu/Attendance/ConsentContext'

export const ManageQCWorkersDialog = () => {
	const [open, setOpen] = React.useState(false)
	const [currentStep, setCurrentStep] = React.useState(1)
	const navigate = useNavigate()
	const { isFeatureEnabled } = useProjectFeaturesContext()
	const handleClickOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation()
		setOpen(true)
	}
	const handleClose = () => {
		setOpen(false)
		setCurrentStep(1)
	}

	const handleShiftEndReport = () => {
		handleClose()
		navigate('/qcReports')
	}

	return (
		<>
			<Button
				onClick={handleClickOpen}
				title='Reassign'
				color='primary'
				sx={{ mr: 4, mt: 1, minWidth: '3rem' }}
				variant='outlined'
			>
				<ManageAccountsIcon sx={{ fontSize: '1.6rem' }} color='primary' />
			</Button>
			<ConsentProvider>
				<Dialog
					open={open}
					onClose={handleClose}
					maxWidth={
						currentStep === 1 ? 'xs' : currentStep === 3 && !isFeatureEnabled(2) ? 'sm' : 'lg'
					}
					fullWidth
				>
					<div
						onClick={(e: React.MouseEvent<HTMLDivElement>) => {
							e.stopPropagation()
						}}
					>
						<DialogTitle>
							{currentStep !== 1 && (
								<IconButton color='default' onClick={() => setCurrentStep(1)} sx={{ mr: 1 }}>
									<ArrowBackIcon />
								</IconButton>
							)}
							{currentStep === 1
								? 'Manage QC Workers'
								: currentStep === 2
								? 'Sign In'
								: currentStep === 3
								? 'Sign Out'
								: currentStep === 4
								? 'QC Assignments'
								: 'Shift End Report'}
						</DialogTitle>
						<IconButton
							onClick={handleClose}
							sx={(theme) => ({
								position: 'absolute',
								right: 8,
								top: 8,
								color: theme.palette.grey[500],
							})}
						>
							<CloseIcon />
						</IconButton>

						{currentStep === 1 ? (
							<DialogContent sx={{ height: 'auto' }}>
								<List>
									<ListItem divider sx={{ width: '100%' }}>
										<ListItemButton onClick={() => setCurrentStep(2)}>
											<ListItemIcon>
												<LoginIcon />
											</ListItemIcon>
											<ListItemText primary={'Sign In'} />
										</ListItemButton>
									</ListItem>
									<ListItem divider sx={{ width: '100%' }}>
										<ListItemButton onClick={() => setCurrentStep(3)}>
											<ListItemIcon>
												<LogoutIcon />
											</ListItemIcon>
											<ListItemText primary={'Sign Out'} />
										</ListItemButton>
									</ListItem>
									<ListItem divider sx={{ width: '100%' }}>
										<ListItemButton onClick={() => setCurrentStep(4)}>
											<ListItemIcon>
												<GroupIcon />
											</ListItemIcon>
											<ListItemText primary={'Assignments'} />
										</ListItemButton>
									</ListItem>
									<ListItem divider sx={{ width: '100%' }}>
										<ListItemButton onClick={handleShiftEndReport}>
											<ListItemIcon>
												<ScheduleIcon />
											</ListItemIcon>
											<ListItemText primary={'Shift End Report'} />
										</ListItemButton>
									</ListItem>
								</List>
							</DialogContent>
						) : currentStep === 2 ? (
							!isFeatureEnabled(2) ? (
								<SignOnDialog handleClose={handleClose} open={currentStep === 2} department='QC' />
							) : (
								<CardSignOnDialog open={currentStep === 2} department='QC' />
							)
						) : currentStep === 3 ? (
							!isFeatureEnabled(2) ? (
								<SignOutDialog
									handleClose={handleClose}
									open={currentStep === 3}
									department='QC'
								/>
							) : (
								<CardSignOutDialog open={currentStep === 3} department='QC' />
							)
						) : currentStep === 4 ? (
							<ForemanQCTables currentStep={currentStep} />
						) : null}
					</div>
				</Dialog>
			</ConsentProvider>
		</>
	)
}

type TablesProps = {
	currentStep: number
}

function ForemanQCTables({ currentStep }: TablesProps) {
	const [qcSelections, setQcSelections] = useState<{ [key: number]: number }>({})
	const [submitting, setSubmitting] = React.useState<boolean>(false)
	const [foremenQC, setForemenQC] = React.useState<ForemanQC[]>([])
	const [qcWorkersOfProject, setQcWorkersOfProject] = React.useState<SmallWorker[]>([])
	useEffect(() => {
		const initialSelections: { [key: number]: number } = {}
		foremenQC.forEach((f) => {
			initialSelections[f.foreman.id] = f.qc?.id ?? 0
		})
		setQcSelections(initialSelections)
		return () => {
			setQcSelections({})
		}
	}, [foremenQC])

	React.useEffect(() => {
		if (currentStep === 4) {
			;(async () => {
				setSubmitting(true)
				const [foremanRes, availableQCs] = await Promise.all([getForemanQC(), getQCs()])
				foremanRes && setForemenQC(foremanRes)
				availableQCs &&
					setQcWorkersOfProject(
						availableQCs.map((qc) => ({
							fullName: `${qc.firstName} ${qc.lastName}`,
							id: qc.id,
						})),
					)
				setSubmitting(false)
			})()
		}
	}, [currentStep])

	const chunkArray = (arr: ForemanQC[], size: number) => {
		return arr.reduce((acc, _, i) => {
			if (i % size === 0) {
				acc.push(arr.slice(i, i + size))
			}
			return acc
		}, [] as ForemanQC[][])
	}
	const chunkedData = chunkArray(foremenQC, 10)
	const dialogWidth =
		foremenQC.length === 0
			? '500px'
			: `${Math.min(Math.ceil(foremenQC.length / 10) * 430, window.innerWidth - 100)}px`
	return (
		<>
			<DialogContent sx={{ height: '70vh' }}>
				<Box sx={{ display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', gap: '10px' }}>
					{chunkedData.map((tableData, index) => (
						<TableContainer
							key={index}
							component={Paper}
							sx={{ flex: '0 0 auto', my: 1, maxWidth: '390px' }}
						>
							<Table sx={{ border: 'ActiveBorder' }}>
								<TableHead sx={{ backgroundColor: '#F7F7F7' }}>
									<TableRow>
										<TableCell align='left' width={'50%'}>
											Foreman
										</TableCell>
										<TableCell align='left' width={'50%'}>
											Assigned QC
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{tableData.map((f) => (
										<TableRow key={f.foreman.id}>
											<TableCell
												align='left'
												sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' } }}
											>
												{f.foreman.fullName}
											</TableCell>
											<TableCell
												align='left'
												padding='none'
												sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' } }}
											>
												{
													<FormControl sx={{ m: 0, width: '100%' }}>
														<Select
															sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' } }}
															multiple={false}
															size='small'
															value={qcSelections[f.foreman.id] || 0}
															onChange={async (e) => {
																const newValue = Number(e.target.value)
																const previousValue = qcSelections[f.foreman.id] || 0
																setSubmitting(true)
																// Optimistically update state
																setQcSelections((prev) => ({
																	...prev,
																	[f.foreman.id]: newValue,
																}))

																try {
																	await updateForemanQC(f.foreman.id, newValue)
																} catch (error) {
																	// Revert back if the update fails
																	setQcSelections((prev) => ({
																		...prev,
																		[f.foreman.id]: previousValue,
																	}))
																} finally {
																	setSubmitting(false)
																}
															}}
														>
															{qcWorkersOfProject.map((qc) => (
																<MenuItem key={`${f.foreman.id}-${qc.id}`} value={qc.id}>
																	{qc.fullName}
																</MenuItem>
															))}
														</Select>
													</FormControl>
												}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					))}
				</Box>
			</DialogContent>
			<DialogActions sx={{ justifyContent: 'end' }}>
				{submitting ? (
					<CircularProgress color='success' sx={{ m: 0 }} />
				) : (
					<Button color='success' variant='contained'>
						Done
					</Button>
				)}
			</DialogActions>
		</>
	)
}
